import { useEffect, useState } from 'react';
import {
  Button,
  Disclosure,
  Modal,
  MonoLabel,
  Pill,
  Row,
  type PillStatus
} from '../primitives';
import {
  useSyncDiagnosticsVM,
  type SyncDiagnosticsStatus,
  type SyncEvent
} from '../../lib/vm/useSyncDiagnosticsVM';

export interface DiagnosticsModalProps {
  open: boolean;
  onClose: () => void;
  /** The signed-in user id — included in the copied diagnostic bundle so
   *  Penrose support can correlate the report with backend logs. */
  viewerId: string | null;
  /** The currently active org id from `<SignedInShell>` — same purpose. */
  activeOrgId: string | null;
}

function formatRelative(ms: number): string {
  if (ms < 0) ms = 0;
  if (ms < 1000) return 'just now';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function formatAbsolute(t: number): string {
  const d = new Date(t);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function statusLabel(s: SyncDiagnosticsStatus): string {
  if (s === 'online') return 'Connected';
  if (s === 'offline') return 'No network';
  return 'Server returned error';
}

function pillStatusFor(httpStatus: number): PillStatus {
  if (httpStatus >= 200 && httpStatus < 300) return 'done';
  return 'blocked';
}

function eventPath(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

function isFailure(status: number): boolean {
  return status === 0 || status >= 400;
}

/**
 * Sync-diagnostics modal — opens from the header's connectivity indicator.
 * Mounts its own `useSyncDiagnosticsVM()` to read the session-wide ring buffer
 * (multiple VM instances share the module-level `recorders` set, so the
 * shell's sibling VM and this modal's VM see the same events). Renders the
 * status / last-successful-contact / Recent activity list with a Show-raw
 * disclosure on failed rows + a Retry-now button.
 */
export function DiagnosticsModal({
  open,
  onClose,
  viewerId,
  activeOrgId
}: DiagnosticsModalProps) {
  const { state, actions } = useSyncDiagnosticsVM();
  const [tick, setTick] = useState(0);
  const [confirmCopyOpen, setConfirmCopyOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>(
    'idle'
  );

  async function handleCopyBundle() {
    const payload = {
      events: state.events,
      browser: navigator.userAgent,
      server_url: import.meta.env.VITE_SUPABASE_URL ?? null,
      user_id: viewerId,
      active_org_id: activeOrgId
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopyStatus('copied');
    } catch {
      setCopyStatus('error');
    } finally {
      setConfirmCopyOpen(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    const iv = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.clearInterval(iv);
    };
  }, [open, onClose]);

  if (!open) return null;
  void tick;

  const now = Date.now();
  const lastSuccessMs =
    state.lastSuccessAt !== null ? Date.parse(state.lastSuccessAt) : null;
  const last =
    lastSuccessMs == null
      ? '—'
      : `${formatRelative(now - lastSuccessMs)} — ${formatAbsolute(lastSuccessMs)}`;

  const failedCount = state.events.filter((e) => isFailure(e.status)).length;
  const newestFirst = state.events.slice().reverse();

  return (
    <div
      className="pn-diag-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="diagnostics-modal"
    >
      <div
        className="pn-diag"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pn-diag-title"
      >
        <header className="pn-diag-head">
          <h2 className="pn-diag-title" id="pn-diag-title">
            Sync diagnostics
          </h2>
          <button
            type="button"
            className="pn-diag-close"
            onClick={onClose}
            aria-label="Close diagnostics"
            data-testid="diagnostics-close"
          >
            ×
          </button>
        </header>
        <div className="pn-diag-body">
          <div className="pn-diag-row">
            <span className="pn-diag-row-label">Status</span>
            <span
              className="pn-diag-row-value"
              data-testid="diagnostics-status"
              data-status={state.status}
            >
              {statusLabel(state.status)}
            </span>
          </div>
          <div className="pn-diag-row">
            <span className="pn-diag-row-label">Last successful contact</span>
            <span
              className="pn-diag-row-value"
              data-testid="diagnostics-last"
            >
              {last}
            </span>
          </div>
          <div className="pn-diag-events" data-testid="diagnostics-events">
            <span className="pn-diag-row-label">Recent activity</span>
            {newestFirst.length === 0 ? (
              <p className="pn-diag-empty" data-testid="diagnostics-empty">
                Everything&rsquo;s healthy.
              </p>
            ) : (
              <ul className="pn-diag-event-list">
                {newestFirst.map((event, idx) => (
                  <DiagEventRow
                    key={`${event.startedAt}-${idx}`}
                    event={event}
                    now={now}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
        <footer className="pn-diag-foot">
          {copyStatus !== 'idle' ? (
            <span
              className="pn-diag-copy-status"
              data-testid="diagnostics-copy-status"
              data-status={copyStatus}
              role="status"
            >
              {copyStatus === 'copied'
                ? 'Bundle copied'
                : "Couldn't copy — check clipboard permissions"}
            </span>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setCopyStatus('idle');
              setConfirmCopyOpen(true);
            }}
            data-testid="diagnostics-copy"
          >
            Copy diagnostic bundle
          </Button>
          <Button
            type="button"
            onClick={() => actions.retryNow()}
            disabled={failedCount === 0}
            data-testid="diagnostics-retry"
          >
            Retry now
          </Button>
        </footer>
      </div>
      <Modal
        open={confirmCopyOpen}
        onClose={() => setConfirmCopyOpen(false)}
        title="Share with Penrose support"
        dataTestId="diagnostics-copy-confirm"
        footer={
          <>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmCopyOpen(false)}
              data-testid="diagnostics-copy-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleCopyBundle}
              data-testid="diagnostics-copy-confirm-go"
            >
              Copy bundle
            </Button>
          </>
        }
      >
        <p>
          This bundle includes your user id and recent activity. Share only with
          Penrose support.
        </p>
      </Modal>
    </div>
  );
}

interface DiagEventRowProps {
  event: SyncEvent;
  now: number;
}

function DiagEventRow({ event, now }: DiagEventRowProps) {
  const path = eventPath(event.url);
  const relative = formatRelative(now - Date.parse(event.startedAt));
  const pillLabel = event.status === 0 ? 'err' : String(event.status);
  const failed = isFailure(event.status);
  const summary = (
    <Row
      className="pn-diag-event"
      template="auto auto minmax(0, 1fr) auto auto"
      data-failed={failed ? '1' : '0'}
    >
      <MonoLabel size="sm">{relative}</MonoLabel>
      <MonoLabel size="sm">{event.method}</MonoLabel>
      <MonoLabel size="sm" className="pn-diag-event-path">
        {path}
      </MonoLabel>
      <Pill status={pillStatusFor(event.status)}>{pillLabel}</Pill>
      <MonoLabel size="sm">{event.durationMs}ms</MonoLabel>
    </Row>
  );
  if (failed) {
    return (
      <li>
        <Disclosure summary={summary} dataTestId="diagnostics-event-failed">
          <div className="pn-diag-event-meta">
            <MonoLabel size="sm">URL: {event.url}</MonoLabel>
            <MonoLabel size="sm">Status: {event.status}</MonoLabel>
          </div>
        </Disclosure>
      </li>
    );
  }
  return <li>{summary}</li>;
}
